import type { Declaration, GlossaProgram, Statement } from '../parser/ast.js';

/**
 * Δεν μπορεί να χρησιμοποιηθεί ολόκληρος πίνακας ως έκφραση στο ΕΠΙΛΕΞΕ —
 * χρειάζεται δείκτης. Βλ. `Interpreter.executeSwitch` για το runtime
 * αντίστοιχο. Χωρίς αυτόν τον έλεγχο, το ΕΠΙΛΕΞΕ απλά δεν θα ταίριαζε ΠΟΤΕ
 * καμία ΠΕΡΙΠΤΩΣΗ (ένα RuntimeArray δεν ισούται ποτέ με καμία απλή τιμή),
 * χωρίς κανένα σφάλμα — μπερδεύει τον μαθητή που δεν καταλαβαίνει γιατί
 * "δεν μπαίνει πουθενά".
 *
 * "Απόρριψη αντί για μάντεμα": ελέγχεται μόνο όταν η έκφραση είναι ένα απλό
 * όνομα ήδη δηλωμένου πίνακα — μόνο έτσι μπορεί μια bare `Identifier`
 * έκφραση να αναφέρεται σε ΟΛΟΚΛΗΡΟ πίνακα.
 */
export interface SwitchArrayDiscriminant {
  readonly name: string;
  readonly line: number;
  readonly column: number;
}

function findDeclaredKind(declarations: readonly Declaration[], name: string): { readonly isArray: boolean } | undefined {
  for (const decl of declarations) {
    if (decl.kind === 'ConstantDeclaration') {
      if (decl.name === name) return { isArray: false };
      continue;
    }
    for (const declarator of decl.declarators) {
      if (declarator.name === name) return { isArray: declarator.dimensions.length > 0 };
    }
  }
  return undefined;
}

function checkStatement(stmt: Statement, declarations: readonly Declaration[]): SwitchArrayDiscriminant | null {
  switch (stmt.kind) {
    case 'SwitchStatement': {
      const discriminant = stmt.discriminant;
      if (discriminant.kind === 'Identifier') {
        const info = findDeclaredKind(declarations, discriminant.name);
        if (info?.isArray) {
          return { name: discriminant.name, line: discriminant.line, column: discriminant.column };
        }
      }
      for (const switchCase of stmt.cases) {
        const result = checkStatements(switchCase.body, declarations);
        if (result !== null) return result;
      }
      if (stmt.elseBody !== null) return checkStatements(stmt.elseBody, declarations);
      return null;
    }
    case 'ForStatement':
      return checkStatements(stmt.body, declarations);
    case 'IfStatement': {
      const thenResult = checkStatements(stmt.thenBranch, declarations);
      if (thenResult !== null) return thenResult;
      for (const branch of stmt.elseIfBranches) {
        const branchResult = checkStatements(branch.body, declarations);
        if (branchResult !== null) return branchResult;
      }
      if (stmt.elseBranch !== null) return checkStatements(stmt.elseBranch, declarations);
      return null;
    }
    case 'WhileStatement':
      return checkStatements(stmt.body, declarations);
    case 'RepeatUntilStatement':
      return checkStatements(stmt.body, declarations);
    case 'AssignmentStatement':
    case 'ReadStatement':
    case 'WriteStatement':
    case 'SwapStatement':
    case 'CallStatement':
      return null;
  }
}

function checkStatements(statements: readonly Statement[], declarations: readonly Declaration[]): SwitchArrayDiscriminant | null {
  for (const stmt of statements) {
    const result = checkStatement(stmt, declarations);
    if (result !== null) return result;
  }
  return null;
}

/**
 * Επιστρέφει την ΠΡΩΤΗ χρήση ολόκληρου πίνακα ως ΕΠΙΛΕΞΕ έκφραση (με σειρά
 * εμφάνισης — κύριο πρόγραμμα πρώτα, μετά κάθε υποπρόγραμμα), ή `null` αν
 * δεν υπάρχει καμία. Κάθε υποπρόγραμμα ελέγχεται με τις ΔΙΚΕΣ ΤΟΥ δηλώσεις
 * (αυστηρά τοπική εμβέλεια — βλ. `findUndeclaredGlossaReference`).
 */
export function findSwitchArrayDiscriminant(program: GlossaProgram): SwitchArrayDiscriminant | null {
  const mainResult = checkStatements(program.body, program.declarations);
  if (mainResult !== null) return mainResult;

  for (const sub of program.subprograms) {
    const subResult = checkStatements(sub.body, sub.declarations);
    if (subResult !== null) return subResult;
  }
  return null;
}
